import type { ComponentProps } from 'react'

import { Icon } from '@/components/icon'
import { Render } from '@/components/render-if'
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/select'
import { cn } from '@/infra'
import type { TChaveNumerica } from '@/types/chaves'

export interface ISeletorMultiploOpcao {
  descricao: string
}

type TPropriedadesDoCampo = Omit<ComponentProps<typeof SelectTrigger>, 'children' | 'className' | 'disabled'>

export interface ISeletorMultiploProps<TOpcao> extends TPropriedadesDoCampo {
  opcoes: TOpcao[]
  idDaOpcao: TChaveNumerica<TOpcao>
  valorDaOpcao: TChaveNumerica<TOpcao>
  idsSelecionados: number[]
  onSelecionar: (id: number) => void
  onRemover: (id: number) => void
  formatarValor: (valor: number) => string
  textoDeEspera: string
  formatarResumoDaSelecao: (quantidade: number) => string
  formatarRotuloDeRemocao: (descricao: string) => string
  grupoDeSelecionados?: string
  className?: string
}

export const SeletorMultiplo = <TOpcao extends ISeletorMultiploOpcao>({
  opcoes,
  idDaOpcao,
  valorDaOpcao,
  idsSelecionados,
  onSelecionar,
  onRemover,
  formatarValor,
  textoDeEspera,
  formatarResumoDaSelecao,
  formatarRotuloDeRemocao,
  grupoDeSelecionados,
  className,
  ...propriedadesDoCampo
}: Readonly<ISeletorMultiploProps<TOpcao>>) => {
  const idDe = (opcao: TOpcao) => Number(opcao[idDaOpcao])
  const valorDe = (opcao: TOpcao) => Number(opcao[valorDaOpcao])

  const selecionados = opcoes.filter((opcao) => idsSelecionados.includes(idDe(opcao)))
  const temSelecao = idsSelecionados.length > 0

  return (
    <div className={cn('font-nunito bg-white', className)}>
      <Select value="" onValueChange={(id) => onSelecionar(Number(id))}>
        <SelectTrigger
          {...propriedadesDoCampo}
          className={cn(
            'h-12 w-full rounded-none border-0 border-t border-neutral-200 px-4 shadow-none aria-invalid:border data-[state=open]:border data-[state=open]:border-sky-700 data-[state=open]:[&_svg]:rotate-180',
            temSelecao ? 'bg-slate-100 data-[placeholder]:bg-slate-100' : 'bg-neutral-50',
          )}
        >
          <span
            className={cn(
              'font-nunito flex-1 text-left text-sm text-neutral-800',
              temSelecao ? 'font-semibold' : 'font-medium',
            )}
          >
            {temSelecao ? formatarResumoDaSelecao(idsSelecionados.length) : textoDeEspera}
          </span>
        </SelectTrigger>

        <SelectContent
          sideOffset={0}
          className={cn(
            'w-(--radix-select-trigger-width) rounded-none border-neutral-200 p-0',
            '**:data-radix-select-viewport:flex **:data-radix-select-viewport:flex-col **:data-radix-select-viewport:gap-2',
          )}
        >
          {opcoes.map((opcao) => {
            const id = idDe(opcao)
            const escolhida = idsSelecionados.includes(id)

            return (
              <SelectItem
                key={id}
                value={String(id)}
                disabled={escolhida}
                className={cn(
                  'font-nunito rounded-none px-4 py-3 font-medium text-neutral-800 data-highlighted:font-semibold *:[span]:last:w-full *:[span]:last:justify-between',
                  escolhida && 'bg-neutral-100 font-semibold',
                )}
              >
                <span className="text-sm">{opcao.descricao}</span>
                <span className="flex items-center gap-2 text-sm text-neutral-700">
                  {formatarValor(valorDe(opcao))}
                  <Render.If condition={escolhida}>
                    {() => (
                      <span className="flex size-6 items-center justify-center rounded bg-green-200">
                        <Icon name="check" className="size-4 text-green-700" />
                      </span>
                    )}
                  </Render.If>
                </span>
              </SelectItem>
            )
          })}
        </SelectContent>
      </Select>

      <Render.If condition={selecionados}>
        {(itens) => (
          <div className="border-t border-neutral-200 px-4 py-3">
            <Render.If condition={grupoDeSelecionados}>
              {(grupo) => <p className="pb-2 text-sm text-neutral-500">{grupo}</p>}
            </Render.If>

            <ul>
              {itens.map((opcao) => (
                <li
                  key={idDe(opcao)}
                  data-testid="seletor-multiplo-selecionado"
                  className="flex items-center justify-between gap-4 border-t border-neutral-200 py-2 first:border-t-0"
                >
                  <span className="text-sm font-semibold text-neutral-800">{opcao.descricao}</span>
                  <span className="flex items-center gap-4">
                    <span className="text-sm font-semibold text-neutral-800">{formatarValor(valorDe(opcao))}</span>
                    <button
                      type="button"
                      aria-label={formatarRotuloDeRemocao(opcao.descricao)}
                      onClick={() => onRemover(idDe(opcao))}
                      className="cursor-pointer text-red-800"
                    >
                      <Icon name="close" className="size-4" />
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Render.If>
    </div>
  )
}
